import { observer } from 'mobx-react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { TableStore } from '@monorepo/controlled/src/stores/table.store';
import { IAdminxStore } from '../../modules/stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { Dropdown, option } from '@monorepo/base/src/components/dropdown/dropdown';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { CampaignsApi } from '../../modules/apis/campaigns.api';
import { fetchCampaignConfigAuditSourceOptions, fetchCampaignConfigChanges } from '../../modules/apis/campaign-config-audit.api';
import { CampaignConfigAuditSourceOption, ConfigChangeEvent } from '../../modules/models/campaign-config-audit.model';
import { CampaignModel } from '../../modules/models/campaign.model';
import {
	formatAuditChangedAt,
	formatAuditRevType,
	toUtcIsoEndOfDay,
	toUtcIsoStartOfDay,
} from '../../modules/utils/dates/campaign-audit-dates';
import { filterConfigChangeRowsByPhase } from '../../modules/utils/campaign-config-audit-filter';
import styles from './campaign-config-audit.module.scss';
import { useDebounce } from '@monorepo/tools/src/lib/hooks/utils/use-debounce';
import { Icon } from '@monorepo/base/src/components/icon/icon';
import { OverflowColumn } from '@monorepo/base/src/components/table/overflow-column/overflow-column';

type ConfigChangeRow = ConfigChangeEvent & {
	subRows?: ConfigChangeRow[];
};

const ALL_SOURCES_LABEL = 'All Sources';
const LOAD_RETRY_DELAY_MS = 5000;
const BE_MAX_ROWS = 3000;

const columnHelper = createColumnHelper<ConfigChangeRow>();

function firstFromSet(val: Set<string> | undefined): string | undefined {
	if (!val || val.size === 0) {
		return undefined;
	}
	return [...val][0];
}

function parseIdFromLabel(label: string): number | null {
	const idPrefix = label.match(/^(\d+)\s*-\s*/);
	if (idPrefix) {
		return Number(idPrefix[1]);
	}
	const inParens = label.match(/\((\d+)\)\s*$/);
	return inParens ? Number(inParens[1]) : null;
}

function parseDateInputToUtcDate(value: string): Date | null {
	// value expected: YYYY-MM-DD (from input type="date")
	const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	if (!m) {
		return null;
	}
	const y = Number(m[1]);
	const mon = Number(m[2]);
	const d = Number(m[3]);
	if (!Number.isFinite(y) || !Number.isFinite(mon) || !Number.isFinite(d)) {
		return null;
	}
	return new Date(Date.UTC(y, mon - 1, d));
}

function buildAllSourcesValue(sourceOptions: CampaignConfigAuditSourceOption[]): string {
	return sourceOptions.map(o => o.sourceName).join(',');
}

export const CampaignConfigAuditPage: FC = observer(() => {
	const { currentRouteWithoutSpecialChars } = useRoute();
	const { settingsStore } = useStores<IAdminxStore>();
	const { tableStore } = usePageSettings(currentRouteWithoutSpecialChars);

	const [sourceOptions, setSourceOptions] = useState<CampaignConfigAuditSourceOption[]>([]);
	const [isSourcesLoading, setIsSourcesLoading] = useState(false);
	const [sourcesLoadMessage, setSourcesLoadMessage] = useState<string>('');
	const [sourcesSelection, setSourcesSelection] = useState<string>('');
	const [appliedKey, setAppliedKey] = useState<string | null>(null);

	const [campaignOptions, setCampaignOptions] = useState<string[]>([]);
	const [isCampaignsLoading, setIsCampaignsLoading] = useState(false);
	const [campaignsLoadMessage, setCampaignsLoadMessage] = useState<string>('');
	const [campaignSelection, setCampaignSelection] = useState<string | null>(null);
	const [campaignId, setCampaignId] = useState<number | null>(null);
	const [hasMore, setHasMore] = useState(false);

	const [phaseOptions, setPhaseOptions] = useState<string[]>([]);
	const [isPhasesLoading, setIsPhasesLoading] = useState(false);
	const [phaseSelection, setPhaseSelection] = useState<string | null>(null);
	const [phaseId, setPhaseId] = useState<number | undefined>(undefined);
	const [actionTypeSelection, setActionTypeSelection] = useState<number | undefined>(undefined);

	const [limitSelection, setLimitSelection] = useState<string>('3000');
	const [fromDate, setFromDate] = useState<string>('');
	const [toDate, setToDate] = useState<string>('');

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string>('');
	const [data, setData] = useState<ConfigChangeRow[]>([]);
	const [hasFetched, setHasFetched] = useState(false);
	const [tableSearch, setTableSearch] = useState<string>('');

	const CAMPAIGN_PHASES_SOURCE = 'campaign_phases';

	const allSourcesValue = useMemo(() => buildAllSourcesValue(sourceOptions), [sourceOptions]);
	const actionTypesOptions = useMemo(() => {
		return [
			{ label: 'All action types', value: 'all' },
			{ label: 'Create', value: 0 },
			{ label: 'Update', value: 1 },
			{ label: 'Delete', value: 2 },
		];
	}, []);

	const sourceDropdownLabels = useMemo(() => {
		return [ALL_SOURCES_LABEL, ...sourceOptions.map(o => o.sourceName)];
	}, [sourceOptions]);

	useEffect(() => {
		if (!settingsStore.getPages().get(currentRouteWithoutSpecialChars)) {
			settingsStore.getPages().set(currentRouteWithoutSpecialChars, { tableStore: new TableStore() });
		}
	}, [currentRouteWithoutSpecialChars, settingsStore]);

	useEffect(() => {
		let cancelled = false;
		let retryTimeout: ReturnType<typeof setTimeout> | null = null;

		const loadSources = (isRetry: boolean) => {
			setIsSourcesLoading(true);
			if (isRetry) {
				setSourcesLoadMessage('Retrying to load sources…');
			}

			fetchCampaignConfigAuditSourceOptions()
				.then(options => {
					if (cancelled) {
						return;
					}
					const list = Array.isArray(options) ? options : [];
					setSourceOptions(list);
					setSourcesSelection(buildAllSourcesValue(list));
					setSourcesLoadMessage('');
				})
				.catch(() => {
					if (cancelled) {
						return;
					}
					if (!isRetry) {
						setSourceOptions([]);
						setSourcesSelection('');
						setSourcesLoadMessage(
							'Something went wrong loading sources. Retrying automatically — this is usually resolved in a few seconds.'
						);
						retryTimeout = setTimeout(() => {
							if (cancelled) {
								return;
							}
							loadSources(true);
						}, LOAD_RETRY_DELAY_MS);
					} else {
						setSourceOptions([]);
						setSourcesSelection('');
						setSourcesLoadMessage('Could not load sources. Please refresh the page or try again later.');
					}
				})
				.finally(() => {
					if (!cancelled) {
						setIsSourcesLoading(false);
					}
				});
		};

		loadSources(false);

		return () => {
			cancelled = true;
			if (retryTimeout) {
				clearTimeout(retryTimeout);
			}
		};
	}, []);

	useEffect(() => {
		let cancelled = false;
		let retryTimeout: ReturnType<typeof setTimeout> | null = null;

		const loadCampaigns = (isRetry: boolean) => {
			setIsCampaignsLoading(true);
			if (isRetry) {
				setCampaignsLoadMessage('Retrying to load campaigns…');
			}

			CampaignsApi.list()
				.then(campaigns => {
					if (cancelled) {
						return;
					}
					setCampaignOptions(campaigns.map(c => `${c.id} - ${c.name}`));
					setCampaignsLoadMessage('');
				})
				.catch(() => {
					if (cancelled) {
						return;
					}
					if (!isRetry) {
						setCampaignOptions([]);
						setCampaignsLoadMessage(
							'Something went wrong loading campaigns. Retrying automatically — this is usually resolved in a few seconds.'
						);
						retryTimeout = setTimeout(() => {
							if (cancelled) {
								return;
							}
							loadCampaigns(true);
						}, LOAD_RETRY_DELAY_MS);
					} else {
						setCampaignOptions([]);
						setCampaignsLoadMessage('Could not load campaigns. Please refresh the page or try again later.');
					}
				})
				.finally(() => {
					if (!cancelled) {
						setIsCampaignsLoading(false);
					}
				});
		};

		loadCampaigns(false);

		return () => {
			cancelled = true;
			if (retryTimeout) {
				clearTimeout(retryTimeout);
			}
		};
	}, []);

	useEffect(() => {
		if (!campaignId) {
			return;
		}
		let cancelled = false;
		setIsPhasesLoading(true);
		CampaignsApi.get(campaignId)
			.then(campaign => {
				if (cancelled) {
					return;
				}
				const c = new CampaignModel(campaign as any);
				setPhaseOptions(c.getPhases().map(p => `${p.id} - ${p.phase.name}`));
			})
			.catch(() => {
				if (!cancelled) {
					setPhaseOptions([]);
				}
			})
			.finally(() => {
				if (!cancelled) {
					setIsPhasesLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [campaignId]);

	const getValidationError = (): string | null => {
		if (!sourcesSelection) {
			return 'Sources are not available yet';
		}
		if (!fromDate && !toDate) {
			return 'From and to date are required';
		}
		if (!fromDate) {
			return 'From date is required';
		}
		if (!toDate) {
			return 'To date is required';
		}
		return null;
	};

	const getEffectiveLimit = () => {
		const n = Number(limitSelection);
		return Number.isFinite(n) && n > 0 ? Math.min(n, BE_MAX_ROWS) : BE_MAX_ROWS;
	};

	const buildParams = () => {
		const params: {
			sources: string;
			from?: string;
			to?: string;
			limit?: number;
			action_type?: number;
			scope_value?: string;
			campaign_phase_id?: number;
		} = {
			sources: sourcesSelection,
			limit: getEffectiveLimit(),
		};

		if (actionTypeSelection !== undefined) {
			params.action_type = actionTypeSelection;
		}

		const fromUtc = fromDate ? parseDateInputToUtcDate(fromDate) : null;
		const toUtc = toDate ? parseDateInputToUtcDate(toDate) : null;

		if (fromUtc) {
			params.from = toUtcIsoStartOfDay(fromUtc);
		}
		if (toUtc) {
			params.to = toUtcIsoEndOfDay(toUtc);
		}

		if (campaignId) {
			params.scope_value = campaignId.toString();
		}
		if (phaseId) {
			params.campaign_phase_id = phaseId;
		}

		return params;
	};

	const validationError = getValidationError();
	const currentKey = validationError ? null : JSON.stringify(buildParams());
	const isDirty = currentKey !== appliedKey;
	const canApply = !validationError && isDirty && !isLoading && !isSourcesLoading;

	const groupByRevision = (rows: ConfigChangeRow[]) => {
		const map = new Map<string, ConfigChangeEvent[]>();
		for (const row of rows) {
			const key = `${row.source}|${row.entity_id}|${row.rev}`;
			if (!map.has(key)) {
				map.set(key, []);
			}
			map.get(key)?.push(row);
		}
		const result = Array.from(map.values()).map(group => {
			if (group.length === 1) {
				return group[0];
			}
			const first = group[0];
			return {
				...first,
				entity_id: first.entity_id,
				field_name: `${group.length} fields`,
				old_value: null,
				new_value: null,
				subRows: group,
			};
		});
		return result;
	};

	const lastSearchRef = useRef<string | null>(null);

	const runFetch = async (search: string) => {
		const validation = getValidationError();
		if (validation) {
			setError(validation);
			return false;
		}
		const params = buildParams();
		const key = JSON.stringify(params);
		lastSearchRef.current = search;

		setIsLoading(true);
		setError('');
		setCampaignsLoadMessage('');
		try {
			const limit = getEffectiveLimit();
			params.limit = limit + 1;
			const res = await fetchCampaignConfigChanges(params, search);
			const rows = Array.isArray(res) ? (res as ConfigChangeRow[]) : [];
			const isTruncated = rows.length > limit;
			setHasMore(isTruncated);
			const visibleRows = isTruncated ? rows.slice(0, limit) : rows;
			const groupedResults = groupByRevision(visibleRows) as ConfigChangeRow[];
			setData(filterConfigChangeRowsByPhase(groupedResults, phaseId));
			setHasFetched(true);
			setAppliedKey(key);
			tableStore?.setPageIndex(0);
			return true;
		} catch (e: unknown) {
			const msg =
				e && typeof e === 'object' && 'data' in e && (e as any).data && typeof (e as any).data.message === 'string'
					? String((e as any).data.message)
					: 'Failed to fetch config changes';
			setData([]);
			setHasMore(false);
			setError(msg);
			setAppliedKey(null);
			return false;
		} finally {
			setIsLoading(false);
		}
	};

	const debouncedTableSearch = useDebounce(tableSearch, 900);

	useEffect(() => {
		const validation = getValidationError();
		if (!hasFetched || validation != null) {
			return;
		}
		const search = debouncedTableSearch.trim();
		if (search === lastSearchRef.current) {
			return;
		}
		runFetch(search);
	}, [debouncedTableSearch]);

	const columns = useMemo(() => {
		const baseCols = [
			columnHelper.display({
				id: 'expand',
				header: '',
				size: 40,
				cell: ({ row }) => {
					if (!row.getCanExpand()) {
						return null;
					}
					return (
						<button
							type="button"
							className={styles.expandButton}
							onClick={e => {
								e.stopPropagation();
								row.toggleExpanded();
							}}
							aria-label="Toggle fields">
							<Icon isMFP size="0.9rem">
								{row.getIsExpanded() ? 'chevron-down' : 'chevron-right'}
							</Icon>
						</button>
					);
				},
			}),
			columnHelper.accessor('entity_id', {
				header: 'ID',
				cell: ({ row }) => {
					if (row.depth > 0) {
						return null;
					}
					const entityId = row.original.entity_id ?? row.subRows?.[0]?.original.entity_id;
					if (entityId == null) {
						return null;
					}
					return <span>{entityId}</span>;
				},
			}),
			columnHelper.accessor('changed_at', {
				header: 'Changed at',
				size: 120,
				cell: ({ row }) => {
					if (row.depth > 0) {
						return null;
					}
					return <span>{formatAuditChangedAt(row.getValue('changed_at'))}</span>;
				},
			}),
			columnHelper.accessor('source', {
				header: 'Source',
				cell: ({ row }) => {
					if (row.depth > 0) {
						return null;
					}
					return <span>{row.getValue('source')}</span>;
				},
			}),
			columnHelper.accessor('rev_type', {
				header: 'Action',
				cell: ({ row }) => {
					if (row.depth > 0) {
						return null;
					}
					return <span>{formatAuditRevType(row.getValue('rev_type'))}</span>;
				},
			}),
			columnHelper.accessor('field_name', {
				header: 'Field',
				cell: ({ row }) => <span>{row.getValue('field_name')}</span>,
			}),
			columnHelper.accessor('old_value', {
				header: 'Old value',
				size: 150,
				cell: ({ row }) => {
					const value = row.getValue('old_value') as string | null;
					return value ? <OverflowColumn data={value} maxWidth={'300px'} /> : <span>—</span>;
				},
			}),
			columnHelper.accessor('new_value', {
				header: 'New value',
				size: 150,
				cell: ({ row }) => {
					const value = row.getValue('new_value') as string | null;
					return value ? <OverflowColumn data={value} maxWidth={'300px'} /> : <span>—</span>;
				},
			}),
		];
		return [...baseCols] as unknown as ColumnDef<ConfigChangeRow, unknown>[];
	}, []);

	const sourcesLoadNotice = sourcesLoadMessage ? <div className={styles.hint}>{sourcesLoadMessage}</div> : null;
	const campaignsLoadNotice = campaignsLoadMessage ? <div className={styles.hint}>{campaignsLoadMessage}</div> : null;

	return (
		<Page>
			<Pagebar>
				<PagebarTitle>Configuration Audit</PagebarTitle>
			</Pagebar>
			<div className={styles.wrapper}>
				<div className={styles.controls}>
					<div className={`${styles.row} ${styles.sourceRow}`}>
						<Dropdown
							label={'Table Sources'}
							isLoading={isSourcesLoading}
							disabled={isSourcesLoading || sourceDropdownLabels.length === 0}
							wrapperClassName={styles.dropdownFieldCompact}
							options={sourceDropdownLabels}
							defaultOption={ALL_SOURCES_LABEL}
							onSelect={(val: Set<option> | undefined) => {
								const label = firstFromSet(val);
								if (!label) {
									return;
								}
								setSourcesSelection(label === ALL_SOURCES_LABEL ? allSourcesValue : label);
								setCampaignSelection(null);
								setCampaignId(null);
								setPhaseOptions([]);
								setPhaseSelection(null);
								setPhaseId(undefined);
							}}
							isCloseIcon={false}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_config_audit_sources')] }}
						/>
						<Dropdown
							className={styles.actionTypeDropdown}
							label="Action Type (optional)"
							placeholder="All action types"
							autocomplete
							autocompletePlaceholder="Search action type"
							options={actionTypesOptions.map(o => o.label)}
							defaultOption={
								actionTypeSelection === undefined
									? 'All action types'
									: (actionTypesOptions.find(o => o.value === actionTypeSelection)?.label ?? 'All action types')
							}
							wrapperClassName={styles.dropdownFieldCompact}
							onSelect={(val: Set<option> | undefined) => {
								const label = firstFromSet(val);
								if (!label || label === 'All action types') {
									setActionTypeSelection(undefined);
									return;
								}
								const match = actionTypesOptions.find(o => o.label === label);
								setActionTypeSelection(typeof match?.value === 'number' ? match.value : undefined);
							}}
							isCloseIcon={true}
						/>
					</div>

					<div className={`${styles.row}`}>
						{sourcesSelection === CAMPAIGN_PHASES_SOURCE ? (
							<div className={`${styles.row} ${styles.phaseRow}`}>
								<Dropdown
									label="Campaign (for phases)"
									placeholder="Select campaign"
									autocomplete
									autocompletePlaceholder="Search by name or campaign ID"
									isLoading={isCampaignsLoading}
									disabled={isCampaignsLoading || campaignOptions.length === 0}
									options={campaignOptions}
									defaultOption={campaignSelection ?? undefined}
									wrapperClassName={styles.dropdownFieldCampaign}
									onSelect={(val: Set<option> | undefined) => {
										const label = firstFromSet(val);
										if (!label) {
											setCampaignSelection(null);
											setCampaignId(null);
											setPhaseOptions([]);
											setPhaseSelection(null);
											setPhaseId(undefined);
											setIsPhasesLoading(false);
											return;
										}
										setCampaignSelection(label);
										setCampaignId(parseIdFromLabel(label));
										setPhaseOptions([]);
										setPhaseSelection(null);
										setPhaseId(undefined);
									}}
									isCloseIcon={true}
									debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_config_audit_campaign')] }}
								/>
								<Dropdown
									label="Phase (optional)"
									placeholder="All phases"
									autocomplete
									autocompletePlaceholder="Search phase"
									isLoading={isPhasesLoading}
									disabled={isPhasesLoading || !campaignId}
									options={phaseOptions}
									defaultOption={phaseSelection ?? undefined}
									wrapperClassName={styles.dropdownFieldPhase}
									onSelect={(val: Set<option> | undefined) => {
										const label = firstFromSet(val);
										if (!label) {
											setPhaseSelection(null);
											setPhaseId(undefined);
											return;
										}
										setPhaseSelection(label);
										const id = parseIdFromLabel(label);
										setPhaseId(id === null ? undefined : id);
									}}
									isCloseIcon={true}
									debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_config_audit_phase')] }}
								/>
							</div>
						) : null}
					</div>

					<div className={`${styles.row} ${styles.dateRow}`}>
						<Input
							topLabel="From date"
							type="date"
							value={fromDate}
							onValue={setFromDate}
							className={styles.dateField}
							inputWrapperClassName={styles.auditInputWrapper}
						/>
						<Input
							topLabel="To date"
							type="date"
							value={toDate}
							onValue={setToDate}
							className={styles.dateField}
							inputWrapperClassName={styles.auditInputWrapper}
						/>
						<Input
							topLabel="Limit"
							type="number"
							value={limitSelection}
							onValue={setLimitSelection}
							isId={true}
							className={styles.limitField}
							inputWrapperClassName={styles.auditInputWrapper}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_config_audit_limit')] }}
						/>
					</div>
					{error ? <div className={styles.error}>{error}</div> : null}
					{validationError && !isSourcesLoading && (fromDate || toDate) ? (
						<div className={styles.hint}>{validationError}</div>
					) : null}
					{hasFetched && isDirty && !validationError && !isLoading && !error ? (
						<div className={styles.hint}>Filters changed — press Apply to refresh.</div>
					) : null}
					{sourcesLoadNotice}
					{campaignsLoadNotice}
				</div>

				<div className={styles.table}>
					<div className={styles.tableSearchRow}>
						<Input
							placeholder="Search results…"
							value={tableSearch}
							onValue={setTableSearch}
							icon="search-sm"
							removeLabel={true}
							inputWrapperClassName={styles.auditInputWrapper}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_config_audit_search')] }}
						/>
					</div>
					<div className={styles.buttonRow}>
						<PrimaryButton disabled={!canApply} onClick={() => runFetch(tableSearch.trim())} className={styles.applyButton}>
							Apply
						</PrimaryButton>
					</div>
					{hasMore ? <div className={styles.hint}>More rows to be seen. Increase limit.</div> : null}
					<ControlledTable
						currentRoute={currentRouteWithoutSpecialChars}
						columns={columns}
						data={data}
						isLoading={isLoading}
						skipFilter={true}
						isGlobalFilter={false}
						removeColumnsPicker={true}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_config_audit_table')] }}>
						{() => {
							return {};
						}}
					</ControlledTable>
					{!isLoading && !error && data.length === 0 && hasFetched ? (
						<div className={styles.hint}>
							{lastSearchRef.current ? 'No rows match your search.' : 'No config changes found for these filters.'}
						</div>
					) : null}
				</div>
			</div>
		</Page>
	);
});
